import React from 'react';
import { scrollToSection } from '../lib/utils';
import { RESUME_URL } from '../data/mockData';

const Hero3D = ({ data }) => {
  const { personalInfo } = data;

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative pt-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      {/* Light overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-blue-50/80 z-0"></div>

      {/* Decorative Background Elements */}
      {/* Large soft circle - top right */}
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-200/30 blur-3xl z-0"></div>
      {/* Large soft circle - bottom left */}
      <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-indigo-200/20 blur-3xl z-0"></div>
      {/* Medium circle - top left */}
      <div className="absolute top-40 left-10 w-32 h-32 rounded-full bg-sky-200/30 blur-2xl z-0"></div>
      {/* Small circle - right side */}
      <div className="absolute top-1/3 right-20 w-24 h-24 rounded-full bg-blue-100/50 blur-xl z-0"></div>
      {/* Decorative dots pattern */}
      <div className="absolute top-20 right-1/4 w-2 h-2 rounded-full bg-blue-400/40 z-0"></div>
      <div className="absolute top-32 right-1/3 w-1.5 h-1.5 rounded-full bg-indigo-400/30 z-0"></div>
      <div className="absolute bottom-40 left-1/4 w-2 h-2 rounded-full bg-sky-400/30 z-0"></div>
      <div className="absolute bottom-32 right-1/3 w-1 h-1 rounded-full bg-blue-500/30 z-0"></div>

      <div className="container mx-auto px-6 relative z-10 w-full min-w-0 max-w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto min-w-0">
          {/* Left Content */}
          <div className="text-center lg:text-left animate-fade-in-up min-w-0 max-w-full">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-green-700 text-sm font-medium">Open to Internships &amp; Full-time Roles</span>
            </div>

            <h1 className="hero-title mb-6">
              Hi, I'm <br />
              <span className="text-blue-600">{personalInfo?.name}</span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-xl">
              {personalInfo?.bio}
            </p>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 justify-center lg:justify-start">
              <button
                onClick={() => scrollToSection('projects')}
                className="btn-primary"
              >
                View My Work
                <i className="fas fa-arrow-right ml-2"></i>
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-secondary"
              >
                Get In Touch
              </button>
              <a
                href={RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <i className="fas fa-file-lines"></i>
                View Resume
              </a>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 mt-8 justify-center lg:justify-start">
              <a
                href={personalInfo?.social?.github}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                <i className="fab fa-github text-xl text-slate-600"></i>
              </a>
              <a
                href={personalInfo?.social?.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                <i className="fab fa-linkedin-in text-xl text-slate-600"></i>
              </a>
              <a
                href={`https://mail.google.com/mail/?view=cm&fs=1&to=${personalInfo?.email}`}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                <i className="fas fa-envelope text-xl text-slate-600"></i>
              </a>
            </div>
          </div>

          {/* Right Content - Profile Image */}
          <div className="flex justify-center animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 blur-2xl opacity-30 animate-pulse"></div>
              <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-[450px] lg:h-[450px] rounded-full overflow-hidden border-4 border-blue-500/30 shadow-2xl">
                <img
                  src="/profile-new.jpg"
                  alt={personalInfo?.name}
                  className="w-full h-full object-cover object-[35%_50%]"
                />
              </div>

              {/* Floating Tech Icons */}
              <div className="absolute -top-4 -right-4 w-14 h-14 bg-white shadow-lg rounded-xl border border-slate-100 hidden sm:flex items-center justify-center animate-bounce" style={{ animationDuration: '3s' }}>
                <i className="fab fa-react text-blue-500 text-2xl"></i>
              </div>
              <div className="absolute bottom-20 -right-6 w-12 h-12 bg-white shadow-lg rounded-xl border border-slate-100 hidden sm:flex items-center justify-center animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                <i className="fab fa-node text-green-500 text-xl"></i>
              </div>
              <div className="absolute top-1/2 -left-8 -translate-y-1/2 w-14 h-14 bg-white shadow-lg rounded-xl border border-slate-100 hidden sm:flex items-center justify-center animate-bounce" style={{ animationDuration: '3.6s', animationDelay: '0.2s' }}>
                <i className="fas fa-rocket text-teal-500 text-2xl"></i>
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-14 h-14 bg-white shadow-lg rounded-xl border border-slate-100 hidden sm:flex items-center justify-center animate-bounce" style={{ animationDuration: '3.4s', animationDelay: '0.5s' }}>
                <i className="fas fa-robot text-violet-500 text-2xl"></i>
              </div>
              <div className="absolute -top-4 left-8 w-11 h-11 bg-white shadow-lg rounded-xl border border-slate-100 hidden sm:flex items-center justify-center animate-bounce" style={{ animationDuration: '4s', animationDelay: '1.2s' }}>
                <i className="fab fa-python text-blue-600 text-lg"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <button
          onClick={() => scrollToSection('about')}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <i className="fas fa-chevron-down text-2xl"></i>
        </button>
      </div>
    </section>
  );
};

export default Hero3D;
